import { API_BASE_URL, getAuthToken } from '../utils/api';

const base = `${API_BASE_URL}/price-book`;

function headers() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getAuthToken()}`,
  };
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      msg = body?.message || msg;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

export type QuotePolicyModel = 'THRESHOLD' | 'ALWAYS_ADMIN' | 'TRUST';

export interface PriceBookCategory {
  id: string;
  companyId: string;
  name: string;
  sortOrder: number;
  active: boolean;
}

export interface PriceBookItem {
  id: string;
  companyId: string;
  categoryId?: string | null;
  sku?: string | null;
  name: string;
  description?: string | null;
  pricingModel: 'FIXED' | 'HOURLY' | 'RANGE';
  unit: string;
  unitPrice: number;
  minPrice?: number | null;
  maxPrice?: number | null;
  laborHours?: number | null;
  vatApplies: boolean;
  active: boolean;
  sortOrder: number;
  notes?: string | null;
  category?: PriceBookCategory | null;
}

export interface QuotePolicy {
  id: string;
  companyId: string;
  model: QuotePolicyModel;
  thresholdAmount?: number | null;
  maxDiscountPct: number;
  validDays: number;
  allowCustomItems: boolean;
  requireCustomerSignature: boolean;
  autoConvertOnAcceptance: boolean;
  notifyAdminsOnSubmit: boolean;
}

export const priceBookService = {
  listCategories(): Promise<PriceBookCategory[]> {
    return fetch(`${base}/categories`, { headers: headers() }).then((r) =>
      handle<PriceBookCategory[]>(r),
    );
  },
  createCategory(input: { name: string; sortOrder?: number }): Promise<PriceBookCategory> {
    return fetch(`${base}/categories`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(input),
    }).then((r) => handle<PriceBookCategory>(r));
  },
  updateCategory(
    id: string,
    patch: { name?: string; sortOrder?: number; active?: boolean },
  ): Promise<PriceBookCategory> {
    return fetch(`${base}/categories/${id}`, {
      method: 'PATCH',
      headers: headers(),
      body: JSON.stringify(patch),
    }).then((r) => handle<PriceBookCategory>(r));
  },
  deleteCategory(id: string): Promise<{ success: boolean }> {
    return fetch(`${base}/categories/${id}`, {
      method: 'DELETE',
      headers: headers(),
    }).then((r) => handle<{ success: boolean }>(r));
  },

  listItems(opts: { includeInactive?: boolean; search?: string } = {}): Promise<PriceBookItem[]> {
    const qs = new URLSearchParams();
    if (opts.includeInactive) qs.set('includeInactive', 'true');
    if (opts.search) qs.set('search', opts.search);
    return fetch(`${base}/items?${qs.toString()}`, { headers: headers() }).then((r) =>
      handle<PriceBookItem[]>(r),
    );
  },
  createItem(input: Partial<PriceBookItem>): Promise<PriceBookItem> {
    return fetch(`${base}/items`, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(input),
    }).then((r) => handle<PriceBookItem>(r));
  },
  updateItem(id: string, patch: Partial<PriceBookItem>): Promise<PriceBookItem> {
    return fetch(`${base}/items/${id}`, {
      method: 'PATCH',
      headers: headers(),
      body: JSON.stringify(patch),
    }).then((r) => handle<PriceBookItem>(r));
  },
  deleteItem(id: string): Promise<{ success: boolean }> {
    return fetch(`${base}/items/${id}`, {
      method: 'DELETE',
      headers: headers(),
    }).then((r) => handle<{ success: boolean }>(r));
  },

  getPolicy(): Promise<QuotePolicy> {
    return fetch(`${base}/policy`, { headers: headers() }).then((r) =>
      handle<QuotePolicy>(r),
    );
  },
  updatePolicy(patch: Partial<QuotePolicy>): Promise<QuotePolicy> {
    return fetch(`${base}/policy`, {
      method: 'PATCH',
      headers: headers(),
      body: JSON.stringify(patch),
    }).then((r) => handle<QuotePolicy>(r));
  },
};
